import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles, MessageSquare } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { AGENTS } from "@/constants/testIds";

/** Right-hand playground: chat with the saved agent to test responses.
 * Owns its own conversation state; parent just supplies the agentId + lead. */
export function AgentPlayground({ agentId, lead }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [convId, setConvId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || !agentId) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/public/agents/${agentId}/chat`, {
        message: text,
        conversation_id: convId,
      });
      setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, booking: data.booking }]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setMessages([]); setConvId(null); };

  return (
    <div className="lg:col-span-2">
      <div className="bg-white border border-neutral-200 sticky top-6">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="overline mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Playground
            </div>
            <div className="font-display text-lg font-bold">Test the agent</div>
          </div>
          {messages.length > 0 && (
            <button onClick={reset} className="text-xs text-neutral-500 hover:text-black">Reset</button>
          )}
        </div>
        {!agentId ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
            Save the agent first, then chat here to test it live.
          </div>
        ) : (
          <>
            <div ref={scrollRef} data-testid={AGENTS.playgroundThread} className="h-[420px] overflow-y-auto p-4 bg-neutral-50 flex flex-col gap-2">
              {messages.length === 0 && (
                <div className="text-xs text-neutral-400 text-center py-8">Say hi to your agent to see how it responds.</div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 text-sm border ${m.role === "user" ? "bg-black text-white border-black" : "bg-white border-neutral-200"}`}>
                    <div className="whitespace-pre-wrap">{m.content}</div>
                    {m.booking && (
                      <div className="mt-2 pt-2 border-t border-white/20 text-xs">
                        ✓ Booking captured: {m.booking.name} · {m.booking.phone}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] px-3 py-2 text-sm bg-white border border-neutral-200 opacity-60">Thinking…</div>
                </div>
              )}
            </div>
            <form onSubmit={send} className="p-3 border-t border-neutral-200 flex gap-2">
              <input
                data-testid={AGENTS.playgroundInput}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black"
                placeholder="Type as a visitor…"
                disabled={busy}
              />
              <button
                data-testid={AGENTS.playgroundSend}
                disabled={busy || !input.trim()}
                className="lf-btn-primary text-sm px-4 flex items-center gap-1"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            <div className="px-4 py-3 border-t border-neutral-200 text-xs font-mono text-neutral-500 bg-neutral-50">
              Widget URL:{" "}
              <span className="break-all">{API}/widget.js?lead_id={lead?.id}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
